/**
 * Step 4 — Policy Rules [TYPE A — Recommended]
 *
 * Embeds SettingsPage showing ONLY the "Working Hours & Policies" tab.
 * All other tabs (submissions-deadlines, general, overtime, utilisation) are hidden.
 * The tab is forced active via DOM after mount.
 *
 * Note: Working Hours fields are read-only (sourced from Resource Manager).
 * The policy enforcement rules (overage thresholds, severity levels) are the
 * relevant configuration for this step.
 */

import { useRef, useEffect, useCallback } from 'react';
import { SettingsPage } from '@/components/Pages/SettingsPage';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, SkipForward } from 'lucide-react';

interface Step4PolicyRulesProps {
  onNext: (status: 'completed' | 'skipped') => void;
  onBack: () => void;
}

const SHOW_TAB = 'working-hours';
const HIDE_TABS = ['submissions-deadlines', 'general', 'overtime', 'utilisation'];

function useSettingsPolicyChrome(embedRef: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const apply = () => {
      const container = embedRef.current;
      if (!container) return;

      // 1. Hide PageHeader (first child of space-y-6 root)
      const root = container.querySelector<HTMLElement>('div.space-y-6');
      if (root) {
        const firstChild = root.children[0] as HTMLElement | undefined;
        if (firstChild) firstChild.style.display = 'none';
        root.style.paddingBottom = '0';
      }

      // 2. Hide unwanted tab triggers
      HIDE_TABS.forEach((tabVal) => {
        const trigger = container.querySelector<HTMLElement>(
          `[role="tab"][data-value="${tabVal}"]`,
        );
        if (trigger) trigger.style.display = 'none';
      });

      // Also target by data-state and value attr pattern used by shadcn Tabs
      container.querySelectorAll<HTMLElement>('[role="tab"]').forEach((tab) => {
        const val = tab.getAttribute('data-value') ?? tab.getAttribute('value');
        if (val && HIDE_TABS.includes(val)) {
          tab.style.display = 'none';
        }
      });

      // 3. Click the working-hours tab trigger to make it active
      const activeTab = container.querySelector<HTMLElement>(
        `[role="tab"][data-value="${SHOW_TAB}"], [role="tab"][value="${SHOW_TAB}"]`,
      );
      if (activeTab && activeTab.getAttribute('data-state') !== 'active') {
        activeTab.click();
      }

      // 4. Hide fixed footer
      container.querySelectorAll<HTMLElement>('div.fixed').forEach((el) => {
        const style = window.getComputedStyle(el);
        if (style.bottom === '0px') {
          el.style.display = 'none';
          el.setAttribute('data-wizard-hidden', 'true');
        }
      });
    };

    apply();
    const t1 = setTimeout(apply, 100);
    const t2 = setTimeout(apply, 400);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      embedRef.current
        ?.querySelectorAll<HTMLElement>('[data-wizard-hidden="true"]')
        .forEach((el) => {
          el.style.display = '';
          el.removeAttribute('data-wizard-hidden');
        });
    };
  }, [embedRef]);
}

export function Step4PolicyRules({ onNext, onBack }: Step4PolicyRulesProps) {
  const embedRef = useRef<HTMLDivElement>(null);
  useSettingsPolicyChrome(embedRef);

  const triggerSave = useCallback(() => {
    const container = embedRef.current;
    if (!container) return;
    const saveBtn = container.querySelector<HTMLButtonElement>(
      '[data-wizard-hidden="true"] button:not([disabled]):last-of-type',
    );
    saveBtn?.click();
  }, []);

  const handleNext = useCallback(() => {
    triggerSave();
    onNext('completed');
  }, [triggerSave, onNext]);

  const handleSkip = useCallback(() => {
    onNext('skipped');
  }, [onNext]);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Embedded settings panel — policy tab only */}
      <div className="flex-1 min-h-0 overflow-hidden px-3 pt-3 pb-1">
        <div
          className="flex flex-col h-full min-h-0 border border-border rounded-lg bg-white overflow-hidden"
          style={{ transform: 'translateZ(0)', willChange: 'transform' }}
        >
          <div ref={embedRef} className="flex-1 min-h-0 overflow-y-auto p-4">
            <SettingsPage />
          </div>
        </div>
      </div>

      {/* Wizard bottom bar */}
      <div className="shrink-0 border-t bg-white px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground max-w-xl">
            Set overage thresholds and policy severity before your first timesheets are submitted.
            Working hours are sourced automatically from Resource Manager.
          </p>
          <div className="flex items-center gap-3 shrink-0">
            <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              className="gap-1 text-muted-foreground"
            >
              <SkipForward className="h-4 w-4" />
              Skip for now
            </Button>
            <Button
              size="sm"
              onClick={handleNext}
              className="gap-1 bg-[#00796B] hover:bg-[#00695C] text-white"
            >
              Save & Continue
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
